import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';

// For JavaScript data types please reference the documentation below:
// @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Data_structures
// @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/typeof

/**
 * Capture exception and send to Datadog browser logs and Datadog RUM
 *
 * @param error Error instance, or error-like object. This will accept just about anything and attempt to convert it into an Error that can be sent to Datadog.
 * @param context Optional context to track with error.
 * @param source Optional error source {@link https://docs.datadoghq.com/real_user_monitoring/browser/collecting_browser_errors/?tab=npm#error-attributes}
 */
export function captureException(error: unknown, context?: object | undefined): void {
    // istanbul ignore else
    if (error === null || typeof error === 'undefined') {
        let e = new Error('Unknown error');
        datadogRum.addError(e, context);
        datadogLogs.logger.error(e.message, context, e);
    } else if (error instanceof Error || Object.prototype.hasOwnProperty.call(error, 'stack')) {
        datadogRum.addError(error, context);
        datadogLogs.logger.error((error as Error).message ?? '', context, error as Error);
    } else if (
        typeof error === 'string' ||
        error instanceof String ||
        typeof error === 'number' ||
        error instanceof Number ||
        typeof error === 'bigint' ||
        typeof error === 'symbol'
    ) {
        let e = new Error(String(error));
        datadogRum.addError(e, context);
        datadogLogs.logger.error(e.message, context, e);
    } else if (typeof error === 'object') {
        let e = new Error('Unknown error');
        datadogRum.addError(e, {
            'Unknown data': { data: error },
            ...context,
        });
        datadogLogs.logger.error(
            e.message,
            {
                'Unknown data': { data: error },
                ...context,
            },
            e,
        );
    } else if (typeof error === 'function') {
        let e = new Error(`[Function ${error.name || 'anonymous'}]`);
        datadogRum.addError(e, {
            'Unknown data': { data: error },
            ...context,
        });
        datadogLogs.logger.error(
            e.message,
            {
                'Unknown data': { data: error },
                ...context,
            },
            e,
        );
    } else {
        // We don't know what this is so log it and hope we can
        // follow the stack traces to determine what is causing it
        // in order to handle it better. Ideally this should never
        // happen but we also don't want a case where a captured
        // error will go unreported so we should have it just in case.
        //
        // NOTE: This else statement is untested, if we new how to
        // test it then we wouldn't need it...
        let e = new Error(`Unexpected ${typeof error}`);
        datadogRum.addError(e, {
            'Unknown data': { data: error },
            ...context,
        });
        datadogLogs.logger.error(
            e.message,
            {
                'Unknown data': { data: error },
                ...context,
            },
            e,
        );
    }
}
